import { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { inject } from '@vercel/analytics'
import { captureUtmParams } from '@/utils/utm'

// Initialize Vercel Analytics
inject()
import { Header } from './components/layout/Header'
import { Footer } from './components/layout/Footer'
import { Home } from './pages/Home'
import { Login } from './pages/Login'
import { Register } from './pages/Register'
import { CheckoutSuccess } from './pages/CheckoutSuccess'
import { AuthCallback } from './pages/AuthCallback'
import { Terms } from './pages/Terms'
import { Privacy } from './pages/Privacy'
import { Contact } from './pages/Contact'
import { ForgotPassword } from './pages/ForgotPassword'
import { ResetPassword } from './pages/ResetPassword'
import { NotFound } from './pages/NotFound'
import { ProtectedRoute } from './components/common/ProtectedRoute'
import { Toaster } from './components/common/Toast'
import { ErrorBoundary } from './components/common/ErrorBoundary'

// Lazy-loaded pages
const Dashboard = lazy(() => import('./pages/Dashboard'))
const DashboardFiles = lazy(() => import('./pages/DashboardFiles'))
const DashboardAnalytics = lazy(() => import('./pages/DashboardAnalytics'))
const DashboardSettings = lazy(() => import('./pages/DashboardSettings'))
const Pricing = lazy(() => import('./pages/Pricing'))
const Download = lazy(() => import('./pages/Download'))

function LoadingSpinner() {
  return (
    <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" role="status">
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  )
}

function App() {
  useEffect(() => {
    captureUtmParams()
  }, [])

  return (
    <Router>
      <div className="min-h-screen flex flex-col">
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-white focus:px-4 focus:py-2 focus:text-primary-600 focus:rounded-lg focus:shadow-lg">
          Skip to content
        </a>
        <Header />
        <main id="main-content" className="flex-1">
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/download/:id" element={<Download />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/files" element={
                  <ProtectedRoute>
                    <DashboardFiles />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/analytics" element={
                  <ProtectedRoute>
                    <DashboardAnalytics />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/settings" element={
                  <ProtectedRoute>
                    <DashboardSettings />
                  </ProtectedRoute>
                } />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </main>
        <Footer />
        <Toaster />
      </div>
    </Router>
  )
}

export default App
